import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check, Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import { format, differenceInDays } from 'date-fns';
import { useLang } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InquiryPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [currentStep, setCurrentStep] = useState(1);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1
    first_name: '',
    last_name: '',
    entity_label: '',
    email: '',
    phone: '',
    
    // Step 2
    project_type: 'album',
    num_people: 2,
    need_engineer: 'not_sure',
    instruments: [],
    piano_tuning_required: false,
    estimated_duration: '',
    flexible_dates: false,
    
    // Step 3
    accommodation_needed: 'full_house',
    catering_interest: false,
    special_requests: '',
    message: '',
  });

  const numDays = dateRange.from && dateRange.to ? differenceInDays(dateRange.to, dateRange.from) : 0;

  const checkAvailability = useCallback(async () => {
    if (!dateRange.from || !dateRange.to) return;
    const days = differenceInDays(dateRange.to, dateRange.from);
    if (days < 2) return;
    try {
      const response = await axios.get(`${API}/availability/check`, {
        params: {
          start_date: dateRange.from.toISOString(),
          end_date: dateRange.to.toISOString(),
        },
      });
      setAvailability(response.data);
    } catch (error) {
      console.error('Error checking availability:', error);
    }
  }, [dateRange]);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
          toast.error(lang === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill in all required fields');
          return false;
        }
        return true;
      case 2:
        if (!formData.estimated_duration) {
          toast.error(lang === 'fr' ? 'Veuillez préciser la durée estimée' : 'Please specify estimated duration');
          return false;
        }
        return true;
      case 3:
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const inquiryData = {
        ...formData,
        language: lang,
        preferred_start_date: dateRange.from ? dateRange.from.toISOString() : null,
        preferred_end_date: dateRange.to ? dateRange.to.toISOString() : null,
      };

      const response = await axios.post(`${API}/inquiry/create`, inquiryData);
      
      toast.success(lang === 'fr' ? 'Demande envoyée avec succès !' : 'Inquiry submitted successfully!');
      const emailSent = response.data.email_sent ? 'true' : 'false';
      navigate(`/inquiry-confirmation?id=${response.data.id}&email_sent=${emailSent}`);
    } catch (error) {
      console.error('Inquiry submission error:', error);
      toast.error(error.response?.data?.detail || (lang === 'fr' ? 'Erreur lors de l\'envoi' : 'Failed to submit inquiry'));
      setLoading(false);
    }
  };

  const instrumentOptions = [
    'Vocals', 'Guitar', 'Bass', 'Drums', 'Piano', 'Synthesizers', 
    'Strings', 'Brass', 'Woodwinds', 'Percussion', 'Electronic Production'
  ];

  return (
    <div data-testid="inquiry-page" className="min-h-screen pt-32 pb-24 bg-[#F5F1EC]">
      <div className="px-5 md:px-10 lg:px-14 max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <p className="text-xs uppercase tracking-[0.4em] text-[#E92A3E] mb-6">{t('inquiry.label')}</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#111] mb-6 leading-tight">
            {t('inquiry.headerTitle')}
          </h1>
          <p className="text-base md:text-lg text-[#555] max-w-2xl mx-auto">
            {t('inquiry.headerDesc')}
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-16">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-500 ${
                  currentStep >= step
                    ? 'bg-[#E92A3E] text-white'
                    : 'bg-[#F5F1EC] text-[#888] border border-[#D9D3CC]'
                }`}
              >
                {currentStep > step ? <Check size={20} /> : step}
              </div>
              {step < 4 && (
                <div
                  className={`w-16 md:w-24 h-px transition-all duration-500 ${
                    currentStep > step ? 'bg-[#E92A3E]' : 'bg-[#D9D3CC]'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {/* Step 1: Identity */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-[#F5F1EC] border border-[#D9D3CC] p-8 md:p-12 rounded-sm"
              >
                <button
                  type="button"
                  onClick={handleBack}
                  data-testid="inquiry-back-to-site"
                  className="text-sm text-[#555] hover:text-[#E92A3E] transition-colors mb-6"
                >
                  {t('inquiry.backToSite')}
                </button>
                <h2 className="font-heading text-3xl font-bold text-[#111] mb-8">{t('inquiry.step1')}</h2>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-[#555] mb-3">{t('inquiry.firstName')}</label>
                      <input
                        type="text"
                        data-testid="input-first-name"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-[#D9D3CC] focus:border-[#E92A3E] outline-none px-0 py-4 text-[#111] transition-colors text-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#555] mb-3">{t('inquiry.lastName')}</label>
                      <input
                        type="text"
                        data-testid="input-last-name"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-[#D9D3CC] focus:border-[#E92A3E] outline-none px-0 py-4 text-[#111] transition-colors text-lg"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-[#555] mb-3">{t('inquiry.entityLabel')}</label>
                    <input
                      type="text"
                      data-testid="input-entity"
                      value={formData.entity_label}
                      onChange={(e) => setFormData({ ...formData, entity_label: e.target.value })}
                      className="w-full bg-transparent border-b-2 border-[#D9D3CC] focus:border-[#E92A3E] outline-none px-0 py-4 text-[#111] transition-colors text-lg"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-[#555] mb-3">{t('inquiry.email')}</label>
                      <input
                        type="email"
                        data-testid="input-email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-[#D9D3CC] focus:border-[#E92A3E] outline-none px-0 py-4 text-[#111] transition-colors text-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#555] mb-3">{t('inquiry.phone')}</label>
                      <input
                        type="tel"
                        data-testid="input-phone"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-[#D9D3CC] focus:border-[#E92A3E] outline-none px-0 py-4 text-[#111] transition-colors text-lg"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-12">
                  <button
                    type="button"
                    onClick={handleNext}
                    data-testid="next-step-button"
                    className="bg-[#E92A3E] hover:bg-[#D0212F] text-white rounded-sm font-semibold px-10 py-4 uppercase tracking-wide text-sm flex items-center gap-2 transition-all duration-300"
                  >
                    {t('inquiry.nextStep')} <ChevronRight size={20} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Project Details */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-[#F5F1EC] border border-[#D9D3CC] p-8 md:p-12 rounded-sm"
              >
                <h2 className="font-heading text-3xl font-bold text-[#111] mb-8">{t('inquiry.step2')}</h2>
                <div className="space-y-8">
                  <div>
                    <label className="block text-sm text-[#555] mb-3">{t('inquiry.projectType')}</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['album', 'writing_residency', 'mixing', 'production', 'other'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          data-testid={`project-type-${type}`}
                          onClick={() => setFormData({ ...formData, project_type: type })}
                          className={`p-4 rounded-sm border transition-all duration-300 text-sm font-medium ${
                            formData.project_type === type
                              ? 'border-[#E92A3E] bg-[#E92A3E]/10 text-[#E92A3E]'
                              : 'border-[#D9D3CC] text-[#555] hover:border-[#E92A3E]'
                          }`}
                        >
                          {t('inquiry.projectTypes.' + type) || type}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-[#555] mb-3">{t('inquiry.numPeople')}</label>
                      <input
                        type="number"
                        data-testid="input-num-people"
                        required
                        min="1"
                        max="10"
                        value={formData.num_people}
                        onChange={(e) => setFormData({ ...formData, num_people: parseInt(e.target.value) })}
                        className="w-full bg-transparent border-b-2 border-[#D9D3CC] focus:border-[#E92A3E] outline-none px-0 py-4 text-[#111] transition-colors text-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#555] mb-3">{t('inquiry.estimatedDuration')}</label>
                      <input
                        type="text"
                        data-testid="input-duration"
                        required
                        placeholder={t('inquiry.durationPlaceholder')}
                        value={formData.estimated_duration}
                        onChange={(e) => setFormData({ ...formData, estimated_duration: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-[#D9D3CC] focus:border-[#E92A3E] outline-none px-0 py-4 text-[#111] transition-colors text-lg placeholder:text-[#B5AFA8]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-[#555] mb-3">{t('inquiry.needEngineer')}</label>
                    <div className="flex gap-3">
                      {['yes_full_time', 'no', 'not_sure'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          data-testid={`engineer-${option}`}
                          onClick={() => setFormData({ ...formData, need_engineer: option })}
                          className={`px-6 py-3 rounded-sm border transition-all duration-300 text-sm font-medium ${
                            formData.need_engineer === option
                              ? 'border-[#E92A3E] bg-[#E92A3E]/10 text-[#E92A3E]'
                              : 'border-[#D9D3CC] text-[#555] hover:border-[#E92A3E]'
                          }`}
                        >
                          {t('inquiry.engineerOptions.' + option) || option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-[#555] mb-3">{t('inquiry.instruments')}</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {instrumentOptions.map((instrument) => (
                        <label
                          key={instrument}
                          className={`p-3 rounded-sm border transition-all duration-300 cursor-pointer ${
                            formData.instruments.includes(instrument)
                              ? 'border-[#E92A3E] bg-[#E92A3E]/10 text-[#E92A3E]'
                              : 'border-[#D9D3CC] text-[#555] hover:border-[#E92A3E]'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.instruments.includes(instrument)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, instruments: [...formData.instruments, instrument] });
                              } else {
                                setFormData({
                                  ...formData,
                                  instruments: formData.instruments.filter((i) => i !== instrument),
                                });
                              }
                            }}
                            className="hidden"
                          />
                          <span className="text-sm">{instrument}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.piano_tuning_required}
                        onChange={(e) => setFormData({ ...formData, piano_tuning_required: e.target.checked })}
                        className="w-5 h-5 accent-[#E92A3E]"
                      />
                      <span className="text-[#111]">{t('inquiry.pianoTuning')}</span>
                    </label>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.flexible_dates}
                        onChange={(e) => setFormData({ ...formData, flexible_dates: e.target.checked })}
                        className="w-5 h-5 accent-[#E92A3E]"
                      />
                      <span className="text-[#111]">{t('inquiry.flexibleDates')}</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-between mt-12">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="border border-[#D9D3CC] text-[#111] hover:border-[#E92A3E] transition-colors duration-300 rounded-sm px-10 py-4 uppercase tracking-wide text-sm flex items-center gap-2"
                  >
                    <ChevronLeft size={20} /> {t('inquiry.previous')}
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="bg-[#E92A3E] text-white hover:bg-[#D0212F] transition-colors duration-300 rounded-sm font-semibold px-10 py-4 uppercase tracking-wide text-sm flex items-center gap-2"
                  >
                    {t('inquiry.nextStep')} <ChevronRight size={20} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Logistics */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-[#F5F1EC] border border-[#D9D3CC] p-8 md:p-12 rounded-sm"
              >
                <h2 className="font-heading text-3xl font-bold text-[#111] mb-8">{t('inquiry.step3')}</h2>
                <div className="space-y-8">
                  <div>
                    <label className="block text-sm text-[#555] mb-3">{t('inquiry.accommodation')}</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        type="button"
                        data-testid="accommodation-full-house"
                        onClick={() => setFormData({ ...formData, accommodation_needed: 'full_house' })}
                        className={`p-6 rounded-sm border transition-all duration-300 text-left ${
                          formData.accommodation_needed === 'full_house'
                            ? 'border-[#E92A3E] bg-[#E92A3E]/10 shadow-sm'
                            : 'border-[#D9D3CC] hover:border-[#E92A3E]'
                        }`}
                      >
                        <h3 className="font-heading text-lg font-bold text-[#111] mb-2">{t('inquiry.fullHouse')}</h3>
                        <p className="text-sm text-[#555]">{t('inquiry.fullHouseDesc')}</p>
                      </button>
                      <button
                        type="button"
                        data-testid="accommodation-studio-only"
                        onClick={() => setFormData({ ...formData, accommodation_needed: 'studio_only' })}
                        className={`p-6 rounded-sm border transition-all duration-300 text-left ${
                          formData.accommodation_needed === 'studio_only'
                            ? 'border-[#E92A3E] bg-[#E92A3E]/10 shadow-sm'
                            : 'border-[#D9D3CC] hover:border-[#E92A3E]'
                        }`}
                      >
                        <h3 className="font-heading text-lg font-bold text-[#111] mb-2">{t('inquiry.studioOnly')}</h3>
                        <p className="text-sm text-[#555]">{t('inquiry.studioOnlyDesc')}</p>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-[#555] mb-4">{t('inquiry.selectDates')}</label>
                    <div className="bg-[#F5F1EC] p-6 rounded-sm border border-[#D9D3CC]">
                      <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={2}
                        className="rounded-sm"
                        disabled={{ before: new Date() }}
                      />
                      {numDays > 0 && (
                        <div className="mt-6 p-4 bg-[#EDE8E2] rounded-sm">
                          <p className="text-[#111] mb-2">
                            <strong>{numDays}</strong> {t('inquiry.daysSelected')}
                          </p>
                          {availability && (
                            <p className={`text-sm ${availability.available ? 'text-[#E92A3E]' : 'text-[#555]'}`}>
                              {availability.message} 
                              {availability.pending_inquiries > 0 && ` (${availability.pending_inquiries} ${t('inquiry.pendingInquiry')})`}
                            </p>
                          )}
                          <p className="text-xs text-[#888] mt-2">
                            {t('inquiry.datesDisclaimer')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.catering_interest}
                        onChange={(e) => setFormData({ ...formData, catering_interest: e.target.checked })}
                        className="w-5 h-5 accent-[#E92A3E]"
                      />
                      <span className="text-[#111]">{t('inquiry.catering')}</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm text-[#555] mb-3">{t('inquiry.specialRequests')}</label>
                    <textarea
                      data-testid="input-special-requests"
                      rows="3"
                      value={formData.special_requests}
                      onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                      className="w-full bg-transparent border border-[#D9D3CC] focus:border-[#E92A3E] outline-none p-4 text-[#111] transition-colors rounded-sm"
                      placeholder={t('inquiry.specialRequestsPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#555] mb-3">{t('inquiry.additionalMessage')}</label>
                    <textarea
                      data-testid="input-message"
                      rows="4"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-transparent border border-[#D9D3CC] focus:border-[#E92A3E] outline-none p-4 text-[#111] transition-colors rounded-sm"
                      placeholder={t('inquiry.messagePlaceholder')}
                    />
                  </div>
                </div>

                <div className="flex justify-between mt-12">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="border border-[#D9D3CC] text-[#111] hover:border-[#E92A3E] transition-colors duration-300 rounded-sm px-10 py-4 uppercase tracking-wide text-sm flex items-center gap-2"
                  >
                    <ChevronLeft size={20} /> {t('inquiry.previous')}
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="bg-[#E92A3E] text-white hover:bg-[#D0212F] transition-colors duration-300 rounded-sm font-semibold px-10 py-4 uppercase tracking-wide text-sm flex items-center gap-2"
                  >
                    {t('inquiry.review')} <ChevronRight size={20} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Review & Submit */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-[#F5F1EC] border border-[#D9D3CC] p-8 md:p-12 rounded-sm"
              >
                <h2 className="font-heading text-3xl font-bold text-[#111] mb-8">{t('inquiry.step4')}</h2>
                
                <div className="space-y-6 mb-12">
                  <div className="pb-6 border-b border-[#D9D3CC]">
                    <h3 className="text-sm uppercase tracking-widest text-[#888] mb-3">{t('inquiry.reviewContact')}</h3>
                    <p className="text-[#111]">{formData.first_name} {formData.last_name}</p>
                    {formData.entity_label && <p className="text-[#555] text-sm">{formData.entity_label}</p>}
                    <p className="text-[#555] text-sm">{formData.email} • {formData.phone}</p>
                  </div>

                  <div className="pb-6 border-b border-[#D9D3CC]">
                    <h3 className="text-sm uppercase tracking-widest text-[#888] mb-3">{t('inquiry.reviewProject')}</h3>
                    <p className="text-[#111] mb-2">{t('inquiry.projectTypes.' + formData.project_type) || formData.project_type}</p>
                    <p className="text-[#555] text-sm">{formData.num_people} {lang === 'fr' ? 'personnes' : 'people'} • {formData.estimated_duration}</p>
                    {formData.instruments.length > 0 && (
                      <p className="text-[#555] text-sm mt-2">Instruments: {formData.instruments.join(', ')}</p>
                    )}
                  </div>

                  <div className="pb-6 border-b border-[#D9D3CC]">
                    <h3 className="text-sm uppercase tracking-widest text-[#888] mb-3">{t('inquiry.reviewDates')}</h3>
                    {dateRange.from && dateRange.to ? (
                      <p className="text-[#111]">
                        {format(dateRange.from, 'PPP')} → {format(dateRange.to, 'PPP')} ({numDays} {lang === 'fr' ? 'jours' : 'days'})
                      </p>
                    ) : (
                      <p className="text-[#555]">{t('inquiry.reviewFlexible')}</p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm uppercase tracking-widest text-[#888] mb-3">{t('inquiry.reviewLogistics')}</h3>
                    <p className="text-[#111] mb-2">{formData.accommodation_needed === 'full_house' ? t('inquiry.fullHouse') : t('inquiry.studioOnly')}</p>
                    {formData.catering_interest && <p className="text-[#555] text-sm">• {t('inquiry.reviewCatering')}</p>}
                    {formData.need_engineer !== 'no' && <p className="text-[#555] text-sm">• {t('inquiry.reviewEngineer')} {t('inquiry.engineerOptions.' + formData.need_engineer)}</p>}
                  </div>
                </div>

                <div className="bg-[#E92A3E]/5 border border-[#E92A3E]/20 p-6 rounded-sm mb-8">
                  <p className="text-[#111] text-sm leading-relaxed">
                    {t('inquiry.reviewDisclaimer')}
                  </p>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="border border-[#D9D3CC] text-[#111] hover:border-[#E92A3E] transition-colors duration-300 rounded-sm px-10 py-4 uppercase tracking-wide text-sm flex items-center gap-2"
                  >
                    <ChevronLeft size={20} /> {t('inquiry.previous')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-inquiry-button"
                    className="bg-[#E92A3E] hover:bg-[#D0212F] text-white rounded-sm font-semibold px-12 py-4 uppercase tracking-wide text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                  >
                    {loading ? t('inquiry.submitting') : t('inquiry.submit')}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </div>
  );
};

export default InquiryPage;
